import React, { useState, useEffect, useRef } from 'react';

// ============================================================================
// GMT FACTORY WALKTHROUGH & VIDEO GUIDE (TAS-ENTRY-WALKTHROUGH FULL-SCREEN STYLE)
// Covers ALL 6 Factory Stages with Zero Blank Screens:
// 1. Overview & Factory Isolation Architecture
// 2. Daily Production Entry (Form 5582) - Exact User Screen
// 3. Fabric Inhouse Confirmation (Stage 4_9) - Exact User Screen (List + Detail)
// 4. Sales Contract Cost Sheet Entry - Full Cost Breakdown & BD Approval
// 5. Final Costing & Post-Production Variance Audit
// 6. Shipment Creation & Ex-Factory Handover
// ============================================================================

export const GMT_TRAINING_MODULES = [
  // 1. Overview & Portal Navigation
  {
    id: 'overview',
    stageOrder: 1,
    title: 'Factory Portal Architecture & 5 Core Modules',
    subtitle: 'Stage 1 · Single unified dashboard for all 10 production units & Rishab management',
    estimatedMinutes: '2.5 min',
    type: 'overview',
    role: 'FACTORY USER',
    roleColor: '#006a4e',
    steps: [
      {
        id: 1,
        short: 'Unit Isolation',
        title: 'Factory Portal Login & Strict Unit Isolation',
        durationSec: 9,
        narration: {
          en: 'Welcome to MindForge ERP GMT Factory Training. Factory operators log in with their dedicated factory account and are strictly locked to their own production unit data. Cross-factory data access is completely blocked.',
          hi: 'माइंडफोर्ज ईआरपी जीएमटी फ़ैक्टरी ट्रेनिंग में आपका स्वागत है। फ़ैक्टरी यूज़र अपने तय खाते से लॉगिन करते हैं और केवल अपनी यूनिट का डेटा देख सकते हैं।'
        },
        cursor: { x: 300, y: 55 }
      },
      {
        id: 2,
        short: '5 Action Tiles',
        title: '5 Core Operational Workflow Tiles',
        durationSec: 10,
        narration: {
          en: 'Your factory dashboard features 5 core action tiles: Daily Production Entry, Fabric Inhouse Stage 4_9, Sales Cost Sheet, Final Costing, and Shipment Ex-Factory handovers.',
          hi: 'आपके फ़ैक्टरी डैशबोर्ड पर 5 मुख्य कार्य टाइल्स हैं: दैनिक उत्पादन प्रविष्टि, फ़ैब्रिक इनहाउस स्टेज 4_9, सेल्स कॉस्ट शीट, फाइनल कॉस्टिंग और शिपमेंट।'
        },
        cursor: { x: 130, y: 135 }
      },
      {
        id: 3,
        short: 'Daily Production',
        title: 'Daily Production Entry & Direct Floor Logging',
        durationSec: 10,
        narration: {
          en: 'Clicking Daily Production opens your floor logging sheet directly in Add Mode. Enter today cutting pieces, sewing input, output, and ready garments with real-time audit verification.',
          hi: 'डेली प्रोडक्शन पर क्लिक करने से सीधे एंट्री मोड खुल जाता है। कटिंग, सिलाई इनपुट, उत्पादन और रेडी गारमेंट्स की संख्या तुरंत दर्ज करें।'
        },
        cursor: { x: 130, y: 135 }
      },
      {
        id: 4,
        short: 'TNA Engine Sync',
        title: 'Atomic Central TNA Engine Synchronization',
        durationSec: 10,
        narration: {
          en: 'Any activity recorded inside these factory modules immediately advances central TNA milestones, automatically notifying merchandisers and buyers without manual status chasing.',
          hi: 'फ़ैक्टरी में किया गया कोई भी काम तुरंत मुख्य टीएनए (TNA) में अपडेट हो जाता है। मर्चेंडाइज़र और बायर को लाइव अपडेट मिलता है।'
        },
        cursor: { x: 860, y: 55 }
      }
    ]
  },

  // 2. Daily Production Entry (Form 5582) - EXACT SCREEN MATCH TO USER SCREENSHOTS
  {
    id: 'production',
    stageOrder: 2,
    title: 'Daily Production Entry (Form 5582)',
    subtitle: 'Stage 2 · Floor Tracking: Cutting (PCD), Line Input, Sewing Output & Ready with 24-hr edit lock',
    estimatedMinutes: '3.0 min',
    type: 'production',
    role: 'FACTORY USER',
    roleColor: '#006a4e',
    steps: [
      {
        id: 1,
        short: 'Date & Status',
        title: 'Check Voucher Date & Edit Lock Status',
        durationSec: 9,
        narration: {
          en: 'The voucher date defaults to today with "● Active Today (Editable)". Entries remain editable on the day of creation and lock afterwards to preserve data integrity.',
          hi: 'वाउचर की तारीख आज की रहती है और स्टेटस "Active Today (Editable)" दिखता है। आज का वाउचर एडिटेबल रहता है।'
        },
        cursor: { x: 745, y: 82 }
      },
      {
        id: 2,
        short: 'Pick TAS/700',
        title: 'Select Active TAS Orders (Multi-Select)',
        durationSec: 10,
        narration: {
          en: 'Click on active TAS order pills like TAS/700 to select them. The table below immediately loads all associated styles, shades, and order quantities.',
          hi: 'चल रहे टीएएस जैसे TAS/700 पर क्लिक करें। नीचे टेबल में स्टाइल, शेड्स और ऑर्डर मात्रा तुरंत लोड हो जाएगी।'
        },
        cursor: { x: 50, y: 168 }
      },
      {
        id: 3,
        short: 'Enter Cut/Prod',
        title: 'Enter Today Physical Output in Yellow Columns',
        durationSec: 11,
        narration: {
          en: 'Type today numbers directly into the 4 yellow cells: Today\'s Cutting, Today Issue to sewing line, Today Prod from stitching, and Today Ready finished garments.',
          hi: 'पीले कॉलमों में आज की संख्या भरें: Today\'s Cutting (कटिंग), Today Issue (लाइन इनपुट), Today Prod (सिलाई), और Today Ready (तैयार माल)।'
        },
        cursor: { x: 440, y: 260 }
      },
      {
        id: 4,
        short: 'TNA Milestones',
        title: 'Live Cumulative Totals & TNA Milestone Triggers',
        durationSec: 10,
        narration: {
          en: 'Total Cutting, Total Input, Total Output, and Ready Qty update live. Cutting triggers PCD milestone; line input triggers IN LINE; and 98% output completes LINE OUT in TNA.',
          hi: 'टोटल कटिंग, इनपुट और आउटपुट अपने आप अपडेट होते हैं। कटिंग से PCD, इनपुट से IN LINE और 98% आउटपुट से LINE OUT टीएनए में अपने आप आगे बढ़ जाता है।'
        },
        cursor: { x: 740, y: 260 }
      },
      {
        id: 5,
        short: 'Save Entry',
        title: 'Click Save Entry to Commit Voucher',
        durationSec: 9,
        narration: {
          en: 'Click "Save Entry" at the bottom right. The production voucher is saved instantly with an audit timestamp and synced across the central management war room.',
          hi: 'नीचे दाईं तरफ "Save Entry" पर क्लिक करें। आज का वाउचर सुरक्षित सेव हो जाएगा और सेंट्रल वॉर रूम में तुरंत सिंक होगा।'
        },
        cursor: { x: 900, y: 502 }
      }
    ]
  },

  // 3. Fabric Inhouse (Stage 4_9) - EXACT SCREEN MATCH TO USER'S SCREENSHOTS (LIST & DETAIL)
  {
    id: 'fabinhouse',
    stageOrder: 3,
    title: 'Fabric Inhouse Tracking & Confirmation (Stage 4_9)',
    subtitle: 'Stage 3 · Factory Gate Receipt: Confirming actual mill fabric delivery against mill ETD to unblock cutting',
    estimatedMinutes: '2.5 min',
    type: 'fabinhouse',
    role: 'FACTORY USER',
    roleColor: '#006a4e',
    steps: [
      {
        id: 1,
        short: 'Pending List',
        title: 'Inspect Factory Fab Inhouse Pending Orders (10 TAS)',
        durationSec: 9,
        narration: {
          en: 'Welcome to Fabric Inhouse tracking. Review the 10 pending orders where fabric has been dispatched from mills. Click on TAS/728 to confirm factory gate receipt.',
          hi: 'फ़ैब्रिक इनहाउस स्क्रीन पर आपका स्वागत है। मिल से भेजे गए 10 पेंडिंग ऑर्डर्स देखें। फ़ैक्टरी गेट पर फ़ैब्रिक रिसीव करने के लिए TAS/728 पर क्लिक करें।'
        },
        cursor: { x: 60, y: 235 }
      },
      {
        id: 2,
        short: 'Detail View',
        title: 'TAS/728 Detail & Upstream Milestones Review',
        durationSec: 10,
        narration: {
          en: 'Clicking TAS/728 opens the Fabric Inhouse Confirmation screen. Here you see upstream mill ETD, shipment documentation, and ETA status.',
          hi: 'TAS/728 पर क्लिक करने से फ़ैब्रिक इनहाउस कन्फर्मेशन स्क्रीन खुलती है। यहाँ मिल ईटीडी, शिपमेंट डॉक्स और ईटीए की स्थिति दिखती है।'
        },
        cursor: { x: 140, y: 80 }
      },
      {
        id: 3,
        short: 'Actual Date',
        title: 'Enter Actual Gate Delivery Date & Challan Remark',
        durationSec: 11,
        narration: {
          en: 'Inspect the physical fabric delivery. Enter the actual gate arrival date and optional challan or lot reference number in the confirmation section.',
          hi: 'फ़ैब्रिक डिलीवरी की जाँच करें। कन्फर्मेशन बॉक्स में गेट पर आने की तारीख और चालान या लॉट नंबर दर्ज करें।'
        },
        cursor: { x: 120, y: 155 }
      },
      {
        id: 4,
        short: 'Confirm Inhouse',
        title: 'Click Confirm Fab Inhouse to Advance Stage 4_9',
        durationSec: 10,
        narration: {
          en: 'Click "Confirm Fab Inhouse". MindForge ERP locks the entry, records the gate arrival timestamp, and marks Stage 4_9 complete in Central TNA.',
          hi: '"Confirm Fab Inhouse" पर क्लिक करें। ईआरपी इसे सेव करेगा और मुख्य टीएनए में स्टेज 4_9 को पूरा कर देगा।'
        },
        cursor: { x: 80, y: 195 }
      },
      {
        id: 5,
        short: 'Cutting Unblocked',
        title: 'Milestone 4_9 Synced & PCD Cutting Unblocked',
        durationSec: 9,
        narration: {
          en: 'Fabric inhouse is now confirmed! Milestone 4_16 PCD Cutting is officially unblocked, allowing the production master to schedule daily floor cutting.',
          hi: 'फ़ैब्रिक इनहाउस पूरा हो गया! अब कटिंग (PCD) शुरू करने के लिए रास्ता खुल गया है और प्रोडक्शन मैनेजर कटिंग शेड्यूल कर सकते हैं।'
        },
        cursor: { x: 820, y: 80 }
      }
    ]
  },

  // 4. Sales Contract Cost Sheet
  {
    id: 'costsheet',
    stageOrder: 4,
    title: 'Sales Contract Cost Sheet Entry',
    subtitle: 'Stage 4 · Pre-Production Target Budgeting: Fabric consumption, sewing CM, and trims breakdown',
    estimatedMinutes: '3.0 min',
    type: 'costsheet',
    role: 'FACTORY USER',
    roleColor: '#006a4e',
    steps: [
      {
        id: 1,
        short: 'Target FI Date',
        title: 'Target Fabric Inhouse Date Auto-Calculation',
        durationSec: 9,
        narration: {
          en: 'The system automatically calculates Target Fabric Inhouse Date as Estimated FI minus 20 days buffer for pre-production factory setup.',
          hi: 'सिस्टम अपने आप टारगेट फ़ैब्रिक इनहाउस डेट की गणना करता है (अनुमानित तारीख से 20 दिन पहले)।'
        },
        cursor: { x: 740, y: 40 }
      },
      {
        id: 2,
        short: 'Cost Breakdown',
        title: 'Break Down Fabric, Trims, CM & Washing Cost',
        durationSec: 11,
        narration: {
          en: 'Enter detailed breakdown costs: Fabric price per meter, freight, consumption, sewing CM, Bangladesh trims, Indian trims, and washing cost.',
          hi: 'फ़ैब्रिक, ट्रिम्स, सिलाई सीएम और वाशिंग का विस्तृत खर्च दर्ज करें। कुल लागत और INR दर तुरंत गणना हो जाती है।'
        },
        cursor: { x: 420, y: 180 }
      },
      {
        id: 3,
        short: 'Submit Approval',
        title: 'Submit Cost Sheet to Bangladesh Team for Approval',
        durationSec: 9,
        narration: {
          en: 'Click "Submit for Approval". The cost sheet locks at factory level and immediately routes to the Bangladesh Team approval queue in Dhaka.',
          hi: '"Submit for Approval" पर क्लिक करें। कॉस्ट शीट बीडी टीम की मंजूरी के लिए सीधे ढाका ऑफिस चली जाएगी।'
        },
        cursor: { x: 860, y: 480 }
      }
    ]
  },

  // 5. Final Costing (Exact FinalCostEntry replica)
  {
    id: 'finalcosting',
    stageOrder: 5,
    title: 'Final Costing Entry & Production Cost Lock',
    subtitle: 'Stage 5 · Post-Production Entry: Pre-filled from approved sales cost sheet, adjust actual production costs',
    estimatedMinutes: '3.0 min',
    type: 'finalcosting',
    role: 'FACTORY USER',
    roleColor: '#b5651d',
    steps: [
      {
        id: 1,
        short: 'Pre-filled Grid',
        title: 'Review Pre-Filled Figures from Approved Sales Cost Sheet',
        durationSec: 10,
        narration: {
          en: 'Open TAS order in Final Costing. All figures are pre-filled directly from the approved Sales Contract Cost Sheet.',
          hi: 'फाइनल कॉस्टिंग में TAS ऑर्डर खोलें। सभी आंकड़े स्वीकृत सेल्स कॉस्ट शीट से पहले से भरे हुए मिलेंगे।'
        },
        cursor: { x: 320, y: 160 }
      },
      {
        id: 2,
        short: 'Actual Floor Costs',
        title: 'Update Actual Floor Consumption & Department Costs',
        durationSec: 10,
        narration: {
          en: 'Review Fabric Rate, Actual Floor Consumption, CM, Washing, and Department expenses. Total COG ($) and INR (₹) recalculate automatically.',
          hi: 'फ़ैब्रिक रेट, वास्तविक खपत, सीएम, वाशिंग और विभाग खर्च की समीक्षा करें। कुल COG और INR की गणना अपने आप हो जाएगी।'
        },
        cursor: { x: 500, y: 240 }
      },
      {
        id: 3,
        short: 'Save Final Costing',
        title: 'Save Final Costing for Central BD Dhaka Approval',
        durationSec: 9,
        narration: {
          en: 'Click "Save Final Costing". The final cost sheet is saved with timestamp and queued for BD Dhaka management approval.',
          hi: '"Save Final Costing" पर क्लिक करें। फ़ाइनल कॉस्ट शीट बीडी टीम की मंजूरी के लिए सुरक्षित हो जाएगी।'
        },
        cursor: { x: 860, y: 480 }
      }
    ]
  },

  // 6. Shipment Creation & Ex-Factory Handover (Exact ShipmentView replica)
  {
    id: 'shipment',
    stageOrder: 6,
    title: 'Export Shipment Creation & Shade-Wise Handover',
    subtitle: 'Stage 6 · Factory Ex-Factory Entry: Select approved TAS, enter shade-wise ship quantities, and attach shipping documents',
    estimatedMinutes: '3.0 min',
    type: 'shipment',
    role: 'FACTORY USER',
    roleColor: '#006a4e',
    steps: [
      {
        id: 1,
        short: 'Ex-Factory Date',
        title: 'Confirm Ex-Factory Date & Optional Remarks',
        durationSec: 8,
        narration: {
          en: 'Ex-factory date defaults to today. Add any optional challan, vehicle, or container remark.',
          hi: 'एक्स-फैक्ट्री तारीख अपने आप आज की सेट हो जाती है। आवश्यक रिमार्क या चालान नंबर दर्ज करें।'
        },
        cursor: { x: 200, y: 155 }
      },
      {
        id: 2,
        short: 'Select Approved TAS',
        title: 'Select Approved TAS Eligible for Export Shipment',
        durationSec: 9,
        narration: {
          en: 'Click on approved TAS orders to load shade-wise order quantities. Only TAS with approved Final Costing appear here.',
          hi: 'अनुमोदित TAS ऑर्डर पर क्लिक करें। केवल वही TAS यहाँ दिखेंगे जिनकी फाइनल कॉस्टिंग स्वीकृत हो चुकी है।'
        },
        cursor: { x: 190, y: 220 }
      },
      {
        id: 3,
        short: 'Enter Ship Qty',
        title: 'Enter Shade-Wise Ship Quantities & Verify Balance',
        durationSec: 10,
        narration: {
          en: "Enter today's physical ship quantity for each garment shade. The live balance recalculates in real-time.",
          hi: 'प्रत्येक शेड के लिए आज की शिपिंग मात्रा (Ship Qty) दर्ज करें। बैलेंस अपने आप अपडेट होगा।'
        },
        cursor: { x: 620, y: 320 }
      },
      {
        id: 4,
        short: 'Attach Docs & Save',
        title: 'Attach Soft Copies (Invoice, Packing List) & Save Shipment',
        durationSec: 9,
        narration: {
          en: 'Attach soft copies of Commercial Invoice and Packing List, then click "Save Shipment" to complete ex-factory dispatch.',
          hi: 'कमर्शियल इनवॉइस और पैकिंग लिस्ट अटैच करें, फिर "Save Shipment" पर क्लिक करके शिपमेंट पूरा करें।'
        },
        cursor: { x: 860, y: 485 }
      }
    ]
  }
];

// ============================================================================
// MAIN COMPONENT: FULL SCREEN INTERACTIVE WALKTHROUGH PLAYER
// ============================================================================
export default function GmtTrainingModal({ onClose, initialStage = 1, factory }) {
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('dj_user')) || {};
    } catch { return {}; }
  })();

  const activeFactoryName = factory || currentUser.factoryName || currentUser.factory_name || 'FASHION SUMMIT';
  const [activeIdx, setActiveIdx] = useState(() => Math.max(0, Math.min(GMT_TRAINING_MODULES.length - 1, (Number(initialStage) || 1) - 1)));
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    if (initialStage != null) {
      const targetIdx = Math.max(0, Math.min(GMT_TRAINING_MODULES.length - 1, (Number(initialStage) || 1) - 1));
      setActiveIdx(targetIdx);
      setCurrentStepIndex(0);
      setStepProgress(0);
    }
  }, [initialStage]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [language, setLanguage] = useState('en'); // 'en' | 'hi'
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1, 1.5, 2
  const [isMuted, setIsMuted] = useState(false);
  const [stepProgress, setStepProgress] = useState(0);
  const [ripplePos, setRipplePos] = useState(null);
  const [rippleKey, setRippleKey] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  const timerRef = useRef(null);
  const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
  const utteranceRef = useRef(null);

  const moduleData = GMT_TRAINING_MODULES[activeIdx] || GMT_TRAINING_MODULES[0];
  const step = moduleData.steps[currentStepIndex] || moduleData.steps[0];
  const totalSteps = moduleData.steps.length;

  // Full Screen Responsive auto-scale: fits BOTH width AND height
  useEffect(() => {
    const handleResize = () => {
      if (wrapRef.current) {
        const wrapW = wrapRef.current.clientWidth;
        const wrapH = wrapRef.current.clientHeight;
        const scaleW = (wrapW - 24) / 960;
        const scaleH = (wrapH - 24) / 540;
        setScale(Math.min(1.2, Math.max(0.45, Math.min(scaleW, scaleH))));
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcut: Esc to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const stopSpeech = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
    }
  };

  useEffect(() => {
    return () => {
      stopSpeech();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Audio voice narration synthesis
  const playSpeechForCurrentStep = () => {
    if (isMuted || !synthRef.current) return;
    try {
      synthRef.current.cancel();
      const textToSpeak = step.narration[language] || step.narration.en;
      if (!textToSpeak) return;

      const utt = new SpeechSynthesisUtterance(textToSpeak);
      utt.rate = playbackSpeed;
      utt.lang = language === 'hi' ? 'hi-IN' : 'en-US';

      const voices = synthRef.current.getVoices();
      if (voices && voices.length > 0) {
        if (language === 'hi') {
          const hiVoice = voices.find(v => v.lang.includes('hi') || v.name.toLowerCase().includes('hindi'));
          if (hiVoice) utt.voice = hiVoice;
        } else {
          const enVoice = voices.find(v => (v.lang.includes('en') && (v.name.includes('Google') || v.name.includes('Natural') || v.name.includes('Premium'))));
          if (enVoice) utt.voice = enVoice;
        }
      }

      utteranceRef.current = utt;
      synthRef.current.speak(utt);
    } catch {
      // Speech synthesis fallback
    }
  };

  // Cursor movement & click ripples
  useEffect(() => {
    if (step.cursor) {
      const timer = setTimeout(() => {
        setRipplePos({ x: step.cursor.x, y: step.cursor.y });
        setRippleKey(k => k + 1);
        if (step.id >= (totalSteps - 1)) {
          setShowToast(true);
        } else {
          setShowToast(false);
        }
      }, 700 / playbackSpeed);
      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, activeIdx, playbackSpeed, totalSteps]);

  // Step advancement timer
  useEffect(() => {
    if (!isPlaying) {
      stopSpeech();
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    playSpeechForCurrentStep();
    setStepProgress(0);

    const stepDurationMs = ((step.durationSec || 9) / playbackSpeed) * 1000;
    const intervalMs = 100;
    let elapsedMs = 0;

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      elapsedMs += intervalMs;
      const pct = Math.min(100, Math.round((elapsedMs / stepDurationMs) * 100));
      setStepProgress(pct);

      if (elapsedMs >= stepDurationMs) {
        clearInterval(timerRef.current);
        if (currentStepIndex < totalSteps - 1) {
          setCurrentStepIndex(prev => prev + 1);
        } else {
          setIsPlaying(false);
        }
      }
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeIdx, currentStepIndex, isPlaying, language, playbackSpeed, isMuted, totalSteps]);

  const handlePlayPause = () => {
    if (!isPlaying && currentStepIndex === totalSteps - 1) {
      setCurrentStepIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(prev => !prev);
    }
  };

  const handleStepJump = (idx) => {
    stopSpeech();
    setCurrentStepIndex(idx);
    setStepProgress(0);
    setIsPlaying(true);
  };

  const handleSelectModule = (idx) => {
    stopSpeech();
    setActiveIdx(Number(idx));
    setCurrentStepIndex(0);
    setStepProgress(0);
    setIsPlaying(true);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      background: '#090d16',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes rip { from { transform: scale(0.3); opacity: 0.95; } to { transform: scale(1.6); opacity: 0; } }
        @keyframes blink { 50% { opacity: 0; } }
        @keyframes toastUp { from { transform: translate(-50%, 14px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
        .walk-ctl:hover { filter: brightness(1.2); }
      `}</style>

      {/* ============================================================ */}
      {/* 1. TOP HEADER & SCREEN SELECTOR (FULL SCREEN TOP BAR)         */}
      {/* ============================================================ */}
      <div style={{
        height: 48,
        padding: '0 20px',
        background: '#0f172a',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎥</span>
          <strong style={{ fontSize: 14.5, color: '#f8fafc', letterSpacing: 0.2 }}>
            Video Guide · {moduleData.title}
          </strong>
          <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#0284c7', color: '#ffffff' }}>
            FULL SCREEN SIMULATOR
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Quick Stage Switcher */}
          <select
            value={activeIdx}
            onChange={(e) => handleSelectModule(e.target.value)}
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {GMT_TRAINING_MODULES.map((m, idx) => (
              <option key={m.id} value={idx}>
                Stage {m.stageOrder}: {m.title}
              </option>
            ))}
          </select>

          {/* Language Toggle */}
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
            <button
              onClick={() => setLanguage('en')}
              style={{
                padding: '4px 10px',
                fontSize: 11.5,
                fontWeight: 700,
                border: 'none',
                background: language === 'en' ? '#2563eb' : '#1e293b',
                color: language === 'en' ? '#ffffff' : '#94a3b8',
                cursor: 'pointer'
              }}
            >
              English
            </button>
            <button
              onClick={() => setLanguage('hi')}
              style={{
                padding: '4px 10px',
                fontSize: 11.5,
                fontWeight: 700,
                border: 'none',
                background: language === 'hi' ? '#2563eb' : '#1e293b',
                color: language === 'hi' ? '#ffffff' : '#94a3b8',
                cursor: 'pointer'
              }}
            >
              हिंदी
            </button>
          </div>

          {/* Audio Mute/Unmute */}
          <button
            onClick={() => setIsMuted(m => !m)}
            style={{
              background: isMuted ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)',
              border: isMuted ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6,
              color: isMuted ? '#ef4444' : '#38bdf8',
              padding: '4px 10px',
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
            title={isMuted ? 'Unmute Audio' : 'Mute Audio'}
          >
            <span>{isMuted ? '🔇' : '🔊'}</span>
            <span style={{ fontSize: 11, fontWeight: 600 }}>{isMuted ? 'Muted' : 'Voice'}</span>
          </button>

          {/* Speed Toggles */}
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
            {[1, 1.5, 2].map(sp => (
              <button
                key={sp}
                onClick={() => setPlaybackSpeed(sp)}
                style={{
                  padding: '4px 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  border: 'none',
                  background: playbackSpeed === sp ? '#38bdf8' : '#1e293b',
                  color: playbackSpeed === sp ? '#0f172a' : '#94a3b8',
                  cursor: 'pointer'
                }}
              >
                {sp}×
              </button>
            ))}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            title="Close Full Screen Walkthrough (Esc)"
            style={{
              background: 'rgba(239,68,68,0.2)',
              border: '1px solid rgba(239,68,68,0.4)',
              width: 32,
              height: 32,
              borderRadius: 6,
              color: '#f87171',
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 6
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ============================================================ */}
      {/* 2. TOP STEP RAIL (MATCHING TAS-ENTRY-WALKTHROUGH)             */}
      {/* ============================================================ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${moduleData.steps.length}, minmax(0, 1fr))`,
        gap: 6,
        padding: '8px 20px',
        background: '#0b1120',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0
      }}>
        {moduleData.steps.map((st, i) => {
          const isOn = i === currentStepIndex;
          const isDone = i < currentStepIndex;
          const roleColor = moduleData.roleColor || '#006a4e';

          return (
            <button
              key={st.id}
              onClick={() => handleStepJump(i)}
              style={{
                appearance: 'none',
                border: isOn ? `1.5px solid ${roleColor}` : '1px solid rgba(255,255,255,0.12)',
                background: isOn ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                color: '#f8fafc',
                borderRadius: 6,
                padding: '5px 8px 7px',
                textAlign: 'left',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontFamily: 'monospace', color: isOn ? '#38bdf8' : '#94a3b8' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isOn ? roleColor : (isDone ? '#10b981' : '#64748b'), flexShrink: 0 }} />
                <span>{String(i + 1).padStart(2, '0')} · {moduleData.role}</span>
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                {st.short || st.title}
              </div>
              <div style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                height: 3,
                width: isDone ? '100%' : (isOn ? `${stepProgress}%` : '0%'),
                background: roleColor,
                opacity: isDone ? 0.45 : 1,
                transition: isOn ? 'width 0.1s linear' : 'none'
              }} />
            </button>
          );
        })}
      </div>

      {/* ============================================================ */}
      {/* 3. CENTER VIEWPORT: TRUE FULL-SCREEN AUTO-SCALED STAGE       */}
      {/* ============================================================ */}
      <div
        ref={wrapRef}
        style={{
          flex: 1,
          position: 'relative',
          background: '#040711',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        {/* 960 × 540 Exact Aspect Ratio Stage */}
        <div style={{
          width: 960,
          height: 540,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          background: '#ffffff',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.85)',
          border: '1px solid #cbd5e1',
          color: '#1e293b',
          position: 'relative'
        }}>

          {/* -------------------------------------------------------- */}
          {/* STAGE 1: FACTORY PORTAL OVERVIEW                         */}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'overview' && (
            <div style={{ padding: 18, height: '100%', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#006a4e' }}>🏭 {activeFactoryName} — Factory Operations Portal</div>
                <div style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>Unit: <strong>{activeFactoryName}</strong> (Strict Isolation)</div>
              </div>
              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { title: 'DAILY PRODUCTION ENTRY', desc: 'Real-time cutting, sewing input, output & ready logging with 24-hr edit lock', col: '#006a4e' },
                  { title: 'FABRIC INHOUSE (STAGE 4_9)', desc: 'Record physical mill fabric delivery and unblock cutting scheduling', col: '#1A4F9C' },
                  { title: 'SALES CONTRACT COST SHEET', desc: 'Pre-production target budgeting: Fabric consumption, CM & trims', col: '#b5651d' },
                  { title: 'FINAL COSTING AUDIT', desc: 'Post-production variance audit: actual vs budget consumption', col: '#7c3aed' },
                  { title: 'SHIPMENT & EX-FACTORY', desc: 'Carton list verification, gate pass issuance & handover tracking', col: '#0284c7' }
                ].map(tile => (
                  <div key={tile.title} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: tile.col, marginBottom: 4 }}>{tile.title}</div>
                    <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>{tile.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* STAGE 2: DAILY PRODUCTION ENTRY (EXACT USER SCREENSHOT)  */}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'production' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc', position: 'relative' }}>
              {/* Header Breadcrumb */}
              <div style={{
                padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10,
                background: '#ffffff', borderBottom: '1px solid #e2e8f0'
              }}>
                <span style={{ fontSize: 16, color: '#006a4e', fontWeight: 800 }}>←</span>
                <div style={{
                  width: 26, height: 26, borderRadius: 6, background: '#eef2ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                }}>
                  📋
                </div>
                <div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>
                    Daily Production Entry <span style={{ color: '#64748b', fontWeight: 500 }}>· {activeFactoryName}</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>
                    Form 5582 · Real-time cutting, sewing input, production output & ready tracking
                  </div>
                </div>
              </div>

              {/* Toolbar */}
              <div style={{
                margin: '6px 14px 0', padding: '6px 12px', background: '#ffffff',
                borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
              }}>
                <div style={{
                  background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155',
                  padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700
                }}>
                  ← Back to List
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', fontWeight: 600 }}>
                  <span>Entry Date:</span>
                  <div style={{
                    padding: '2px 8px', borderRadius: 4,
                    border: currentStepIndex === 0 ? '2px solid #2563eb' : '1px solid #cbd5e1',
                    background: currentStepIndex === 0 ? '#eff6ff' : '#ffffff',
                    color: '#0f172a', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6
                  }}>
                    <span>31-10-2026</span>
                    <span>📅</span>
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 10.5, fontWeight: 700,
                    background: '#ecfdf5', color: '#047857',
                    border: currentStepIndex === 0 ? '2px solid #059669' : '1px solid #a7f3d0'
                  }}>
                    ● Active Today (Editable)
                  </span>
                  <span style={{
                    background: 'linear-gradient(135deg, #1A4F9C 0%, #0d2b5c 100%)',
                    color: '#ffffff', padding: '3px 10px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    <span>🎥</span>
                    <span>VIDEO GUIDE</span>
                  </span>
                </div>
              </div>

              {/* TAS Order Picker */}
              <div style={{
                margin: '6px 14px 0', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px'
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>
                    SELECT TAS ORDERS:
                  </span>
                  <input
                    readOnly
                    placeholder="Search TAS number…"
                    value={currentStepIndex >= 1 ? 'TAS/700' : ''}
                    style={{
                      padding: '2px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11,
                      width: 140, background: '#ffffff', color: '#0f172a'
                    }}
                  />
                  <button style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontSize: 10.5, fontWeight: 600 }}>Select All</button>
                  <button style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontSize: 10.5, fontWeight: 600 }}>Clear</button>
                </div>

                {/* 33 Pills from real screen */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                  {[
                    'TAS/700', 'TAS/701', 'TAS/725', 'TAS/726', 'TAS/727', 'TAS/728', 'TAS/756', 'TAS/783',
                    'TAS/784', 'TAS/785', 'TAS/786', 'TAS/805', 'TAS/806', 'TAS/808', 'TAS/826', 'TAS/827',
                    'TAS/828', 'TAS/829', 'TAS/830', 'TAS/831', 'TAS/832', 'TAS/874', 'TAS/885', 'TAS/886',
                    'TAS/887', 'TAS/914', 'TAS/915', 'TAS/916', 'TAS/917', 'TAS/918', 'TAS/920', 'TAS/927', 'TAS/928'
                  ].map(tas => {
                    const isPicked = tas === 'TAS/700' && currentStepIndex >= 1;
                    const isTarget = tas === 'TAS/700' && currentStepIndex === 1;
                    return (
                      <span key={tas} style={{
                        padding: '2px 7px', borderRadius: 12, fontSize: 9.5, fontWeight: 700,
                        border: isTarget ? '2px solid #2563eb' : (isPicked ? '1px solid #1A4F9C' : '1px solid #cbd5e1'),
                        background: isPicked ? '#1A4F9C' : '#ffffff',
                        color: isPicked ? '#ffffff' : '#0f172a',
                        transition: 'all 0.25s'
                      }}>
                        {tas}
                      </span>
                    );
                  })}
                </div>
                <div style={{ fontSize: 9.5, color: '#64748b', fontWeight: 600 }}>
                  {currentStepIndex === 0 ? '0 of 33 selected · 0 shade rows' : '1 of 33 selected · 2 shade rows'}
                </div>
              </div>

              {/* Table with Yellow Columns */}
              <div style={{
                margin: '6px 14px 0', border: '1px solid #e2e8f0', borderRadius: 6, overflowX: 'auto', background: '#ffffff', flex: 1
              }}>
                <table style={{ width: '100%', minWidth: 920, borderCollapse: 'collapse', fontSize: 10.5 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', color: '#475569', borderBottom: '1px solid #e2e8f0', fontWeight: 700, textAlign: 'left' }}>
                      <th style={{ padding: '6px 8px' }}>TAS</th>
                      <th style={{ padding: '6px 8px' }}>Style</th>
                      <th style={{ padding: '6px 8px' }}>Shade</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Order Qty</th>
                      {/* 4 Yellow Columns */}
                      <th style={{ padding: '6px 8px', textAlign: 'center', background: '#fef3c7', color: '#92400e', borderLeft: '1px solid #fde68a' }}>Today's Cutting</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', background: '#fef3c7', color: '#92400e' }}>Today Issue</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', background: '#fef3c7', color: '#92400e' }}>Today Prod</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', background: '#fef3c7', color: '#92400e', borderRight: '1px solid #fde68a' }}>Today Ready</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total Cutting</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total Input</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total Output</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ready Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Step 0: Empty state placeholder */}
                    {currentStepIndex === 0 && (
                      <tr>
                        <td colSpan={12} style={{ padding: '38px 12px', textAlign: 'center', color: '#64748b', fontSize: 11 }}>
                          Pick one or more TAS above to load order rows.
                        </td>
                      </tr>
                    )}

                    {/* Step >= 1: Loaded rows for TAS/700 */}
                    {currentStepIndex >= 1 && [
                      { tas: 'TAS/700', style: 'WOMENS TROUSERS', shade: 'Beige', qty: '1,600', cut: '150', issue: '150', prod: '120', ready: '100', tcut: '150', tinp: '150', tout: '120', trdy: '100' },
                      { tas: 'TAS/700', style: 'WOMENS TROUSERS', shade: 'BLACK/BROWN', qty: '1,600', cut: '200', issue: '180', prod: '150', ready: '120', tcut: '200', tinp: '180', tout: '150', trdy: '120' }
                    ].map((r, rIdx) => {
                      const hasNumbers = currentStepIndex >= 2;
                      const isTyping = currentStepIndex === 2;
                      const isTotalCalc = currentStepIndex >= 3;

                      return (
                        <tr key={r.shade} style={{ borderBottom: '1px solid #f1f5f9', background: rIdx % 2 === 0 ? '#ffffff' : '#fafbfc' }}>
                          <td style={{ padding: '6px 8px', color: '#1A4F9C', fontWeight: 700 }}>{r.tas}</td>
                          <td style={{ padding: '6px 8px', fontWeight: 600, color: '#1e293b' }}>{r.style}</td>
                          <td style={{ padding: '6px 8px', color: '#475569' }}>{r.shade}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{r.qty}</td>

                          {/* Today's Cutting */}
                          <td style={{ padding: '3px 6px', textAlign: 'center', background: '#fffbeb', borderLeft: '1px solid #fef3c7' }}>
                            <input
                              readOnly
                              value={hasNumbers ? r.cut : ''}
                              style={{
                                width: 52, padding: '2px 4px', textAlign: 'center', borderRadius: 4,
                                border: isTyping ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                                background: isTyping ? '#eff6ff' : '#ffffff',
                                fontWeight: 700, fontSize: 10.5, color: '#0f172a'
                              }}
                            />
                          </td>

                          {/* Today Issue */}
                          <td style={{ padding: '3px 6px', textAlign: 'center', background: '#fffbeb' }}>
                            <input
                              readOnly
                              value={hasNumbers ? r.issue : ''}
                              style={{
                                width: 52, padding: '2px 4px', textAlign: 'center', borderRadius: 4,
                                border: isTyping ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                                background: isTyping ? '#eff6ff' : '#ffffff',
                                fontWeight: 700, fontSize: 10.5, color: '#0f172a'
                              }}
                            />
                          </td>

                          {/* Today Prod */}
                          <td style={{ padding: '3px 6px', textAlign: 'center', background: '#fffbeb' }}>
                            <input
                              readOnly
                              value={hasNumbers ? r.prod : ''}
                              style={{
                                width: 52, padding: '2px 4px', textAlign: 'center', borderRadius: 4,
                                border: isTyping ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                                background: isTyping ? '#eff6ff' : '#ffffff',
                                fontWeight: 700, fontSize: 10.5, color: '#0f172a'
                              }}
                            />
                          </td>

                          {/* Today Ready */}
                          <td style={{ padding: '3px 6px', textAlign: 'center', background: '#fffbeb', borderRight: '1px solid #fef3c7' }}>
                            <input
                              readOnly
                              value={hasNumbers ? r.ready : ''}
                              style={{
                                width: 52, padding: '2px 4px', textAlign: 'center', borderRadius: 4,
                                border: isTyping ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                                background: isTyping ? '#eff6ff' : '#ffffff',
                                fontWeight: 700, fontSize: 10.5, color: '#0f172a'
                              }}
                            />
                          </td>

                          {/* Total Cutting */}
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: isTotalCalc ? '#2563eb' : '#64748b' }}>
                            {isTotalCalc ? r.tcut : '0'}
                          </td>

                          {/* Total Input */}
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: isTotalCalc ? '#2563eb' : '#64748b' }}>
                            {isTotalCalc ? r.tinp : '0'}
                          </td>

                          {/* Total Output */}
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: isTotalCalc ? '#2563eb' : '#64748b' }}>
                            {isTotalCalc ? r.tout : '0'}
                          </td>

                          {/* Ready Qty */}
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: isTotalCalc ? '#059669' : '#64748b' }}>
                            {isTotalCalc ? r.trdy : '0'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Bottom Buttons */}
              <div style={{
                padding: '6px 14px 10px', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center'
              }}>
                <button style={{
                  padding: '5px 14px', borderRadius: 5, border: '1px solid #cbd5e1',
                  background: '#ffffff', color: '#334155', fontSize: 11, fontWeight: 600
                }}>
                  Cancel
                </button>
                <button style={{
                  padding: '5px 20px', borderRadius: 5, border: 'none',
                  background: '#006a4e', color: '#ffffff', fontSize: 11.5, fontWeight: 800,
                  boxShadow: currentStepIndex === 4 ? '0 0 14px rgba(0,106,78,0.7)' : '0 2px 4px rgba(0,106,78,0.2)',
                  outline: currentStepIndex === 4 ? '2.5px solid #34d399' : 'none',
                  transform: currentStepIndex === 4 ? 'scale(0.97)' : 'none',
                  transition: 'all 0.2s'
                }}>
                  Save Entry
                </button>
              </div>
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* STAGE 3: FAB INHOUSE TRACKING (EXACT USER SCREENSHOTS)   */}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'fabinhouse' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc', position: 'relative' }}>
              {/* STEP 0: LIST VIEW (EXACT USER SCREENSHOT 2 & 4) */}
              {currentStepIndex === 0 ? (
                <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%', background: '#ffffff', position: 'relative' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
                    <span style={{ fontSize: 16, color: '#1A4F9C' }}>🧵</span>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14.5, color: '#1A4F9C' }}>
                      {activeFactoryName} — Fab Inhouse
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: '#64748b' }}>REPORT · 10 rows</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4, fontWeight: 700 }}>📊 EXCEL</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4, fontWeight: 700 }}>📄 PDF</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4, fontWeight: 700 }}>✉️ EMAIL</span>
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#1A4F9C', color: '#fff', borderRadius: 4, fontWeight: 700 }}>🎥 VIDEO GUIDE</span>
                    </div>
                  </div>

                  {/* 3 KPI Tiles matching user screenshot */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {[
                      { key: 'pending', count: 10, label: 'PENDING FAB INHOUSE', sub: 'fabric dispatched / active · awaiting factory inhouse', color: '#c62828', active: true },
                      { key: 'upcoming', count: 14, label: 'UPCOMING (FAB ETD PENDING)', sub: 'fabric not yet dispatched · pre-inhouse pipeline', color: '#b5651d', active: false },
                      { key: 'completed', count: 8, label: 'INHOUSE COMPLETED', sub: 'fabric received inhouse · ready for cutting', color: '#1b5e20', active: false },
                    ].map(tile => (
                      <div
                        key={tile.key}
                        style={{
                          padding: '8px 12px', borderRadius: 6,
                          border: tile.active ? '1.5px solid #dc2626' : '1px solid #e2e8f0',
                          background: tile.active ? 'rgba(220, 38, 38, 0.02)' : '#ffffff'
                        }}
                      >
                        <div style={{ fontSize: 22, fontWeight: 700, color: tile.color, lineHeight: 1 }}>{tile.count}</div>
                        <div style={{ fontSize: 10, fontWeight: 800, color: tile.color, letterSpacing: 0.8, marginTop: 3 }}>{tile.label}</div>
                        <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>{tile.sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Search Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>
                      PENDING FAB INHOUSE <span style={{ fontWeight: 400, color: '#64748b' }}>· 10 TAS · click a TAS to confirm Fabric Inhouse date</span>
                    </div>
                    <input
                      readOnly
                      placeholder="search TAS / buyer / fabric / merchandise"
                      style={{ width: 220, padding: '3px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10.5 }}
                    />
                  </div>

                  {/* Pending Table matching user screenshot */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflowX: 'auto', flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '1px solid #cbd5e1' }}>
                          <th style={{ padding: '5px 7px' }}>TAS</th>
                          <th style={{ padding: '5px 7px' }}>BUYER</th>
                          <th style={{ padding: '5px 7px' }}>MERCHANDISER</th>
                          <th style={{ padding: '5px 7px' }}>FABRIC</th>
                          <th style={{ padding: '5px 7px', textAlign: 'right' }}>PCS</th>
                          <th style={{ padding: '5px 7px', textAlign: 'right' }}>MTRS</th>
                          <th style={{ padding: '5px 7px' }}>ENQUIRY</th>
                          <th style={{ padding: '5px 7px' }}>FAB ETD</th>
                          <th style={{ padding: '5px 7px' }}>TARGET</th>
                          <th style={{ padding: '5px 7px' }}>FI DATE</th>
                          <th style={{ padding: '5px 7px' }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { tas: 'TAS/728', buyer: 'Lifestyle', merch: 'Jasiya', fabric: 'BQ77B', pcs: '2,052', mtrs: '2,462', enq: '02-04-2026', etd: '19-07-2026 ✓', target: '11-06-2026', fi: '14-06-2026', status: 'DELAYED +142d' },
                          { tas: 'TAS/727', buyer: 'Lifestyle', merch: 'Jasiya', fabric: 'BQ77B', pcs: '2,057', mtrs: '2,674', enq: '02-04-2026', etd: '19-07-2026 ✓', target: '11-06-2026', fi: '28-05-2026', status: 'DELAYED +142d' },
                          { tas: 'TAS/726', buyer: 'Lifestyle', merch: 'Jasiya', fabric: 'XY72543H', pcs: '4,050', mtrs: '4,455', enq: '02-04-2026', etd: '29-07-2026 ✓', target: '11-06-2026', fi: '28-05-2026', status: 'DELAYED +142d' },
                          { tas: 'TAS/725', buyer: 'Lifestyle', merch: 'Jasiya', fabric: 'XY72543H', pcs: '4,114', mtrs: '6,582', enq: '02-04-2026', etd: '29-07-2026 ✓', target: '11-06-2026', fi: '11-06-2026', status: 'DELAYED +142d' },
                          { tas: 'TAS/756', buyer: 'Lifestyle', merch: 'Jasiya', fabric: 'LYD006', pcs: '4,600', mtrs: '5,980', enq: '29-04-2026', etd: '29-07-2026 ✓', target: '08-07-2026', fi: '12-07-2026', status: 'DELAYED +115d' },
                          { tas: 'TAS/830', buyer: 'Style Union', merch: 'Meenakshi', fabric: '100% Elastomultiester', pcs: '14,000', mtrs: '19,740', enq: '13-08-2026', etd: '30-09-2026 ✓', target: '22-10-2026', fi: '26-11-2026', status: 'DELAYED +9d' }
                        ].map((r, rIdx) => (
                          <tr key={r.tas} style={{ borderBottom: '1px solid #f1f5f9', background: r.tas === 'TAS/728' ? '#eff6ff' : (rIdx % 2 === 0 ? '#fff' : '#fafbfc') }}>
                            <td style={{ padding: '5px 7px', color: '#1A4F9C', fontWeight: 700 }}>{r.tas} ›</td>
                            <td style={{ padding: '5px 7px' }}>{r.buyer}</td>
                            <td style={{ padding: '5px 7px' }}>{r.merch}</td>
                            <td style={{ padding: '5px 7px', fontFamily: 'monospace' }}>{r.fabric}</td>
                            <td style={{ padding: '5px 7px', textAlign: 'right' }}>{r.pcs}</td>
                            <td style={{ padding: '5px 7px', textAlign: 'right', fontWeight: 700, color: '#0d9488' }}>{r.mtrs}</td>
                            <td style={{ padding: '5px 7px', color: '#64748b' }}>{r.enq}</td>
                            <td style={{ padding: '5px 7px', color: '#15803d', fontWeight: 700 }}>{r.etd}</td>
                            <td style={{ padding: '5px 7px' }}>{r.target}</td>
                            <td style={{ padding: '5px 7px' }}>{r.fi}</td>
                            <td style={{ padding: '5px 7px' }}><span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#fee2e2', color: '#dc2626' }}>{r.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* STEP >= 1: THE EXACT TAS/728 DETAIL SCREEN (MATCHING USER'S LATEST SCREENSHOT!) */
                <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%', background: '#ffffff', position: 'relative' }}>
                  {/* Top Breadcrumb & Metadata Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16, color: '#006a4e', fontWeight: 800 }}>←</span>
                        <span style={{ fontSize: 16 }}>🧵</span>
                        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: '#006a4e' }}>
                          TAS/728 — Fab Inhouse
                        </div>
                        {currentStepIndex >= 4 ? (
                          <span style={{ padding: '2px 8px', borderRadius: 12, background: '#e8f5e9', color: '#1b5e20', fontSize: 10, fontWeight: 700 }}>
                            INHOUSE COMPLETED ✓
                          </span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: 12, background: '#fff3e0', color: '#b5651d', fontSize: 10, fontWeight: 700 }}>
                            PENDING FAB INHOUSE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
                        Lifestyle · {activeFactoryName} · Merch: Jasiya
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 14, alignItems: 'center', textAlign: 'right' }}>
                      <div>
                        <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>ENQUIRY DATE</div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155' }}>02-04-2026</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>TARGET INHOUSE (+70D)</div>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#006a4e' }}>11-06-2026</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>FI DATE</div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155' }}>14-06-2026</div>
                      </div>
                    </div>
                  </div>

                  {/* 4 Process Cards (Matching User Screenshot) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    <div style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>4_6 FAB ETD</div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#15803d', marginTop: 2 }}>Actual: 19-07-2026</div>
                    </div>
                    <div style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>4_7 SHIPMENT DOCS</div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginTop: 2 }}>Pending</div>
                    </div>
                    <div style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>4_8 FAB ETA</div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#15803d', marginTop: 2 }}>Actual: 31-07-2026</div>
                    </div>
                    <div style={{
                      padding: '6px 10px', borderRadius: 6,
                      border: currentStepIndex >= 4 ? '1.5px solid #16a34a' : '1px solid #e2e8f0',
                      background: currentStepIndex >= 4 ? '#ecfdf5' : '#f8fafc'
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: currentStepIndex >= 4 ? '#16a34a' : '#64748b' }}>4_16 CUTTING (PCD)</div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: currentStepIndex >= 4 ? '#15803d' : '#64748b', marginTop: 2 }}>
                        {currentStepIndex >= 4 ? 'Ready for Cutting ✓' : 'Not started'}
                      </div>
                    </div>
                  </div>

                  {/* 4_9 FABRIC INHOUSE CONFIRMATION CARD (EXACT USER SCREENSHOT) */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff', overflow: 'hidden' }}>
                    <div style={{ padding: '6px 12px', background: 'rgba(0,106,78,0.06)', borderBottom: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 800, color: '#006a4e', fontSize: 11, letterSpacing: 0.6 }}>
                        4_9 FABRIC INHOUSE CONFIRMATION
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#c62828' }}>
                        ⚠️ +142d late vs Target (11-06-2026)
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, padding: '8px 12px' }}>
                      <div>
                        <label style={{ fontSize: 9.5, fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: 3, display: 'block' }}>
                          ACTUAL FABRIC INHOUSE DATE <span style={{ color: '#c62828' }}>*</span>
                        </label>
                        <input
                          readOnly
                          value="31-10-2026"
                          style={{
                            width: '100%', boxSizing: 'border-box', height: 28, padding: '0 8px', borderRadius: 4,
                            border: currentStepIndex === 2 ? '2px solid #2563eb' : '1.5px solid #d4a72c',
                            background: '#fffbeb', fontSize: 11, fontWeight: 700, color: '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 9.5, fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: 3, display: 'block' }}>
                          REMARK / LOT / CHALLAN REFERENCE
                        </label>
                        <input
                          readOnly
                          value={currentStepIndex >= 2 ? 'Challan #CH-8821 · 52 Rolls Received & QC Passed' : ''}
                          placeholder="Optional challan no, roll count, or fabric receipt note…"
                          style={{
                            width: '100%', boxSizing: 'border-box', height: 28, padding: '0 8px', borderRadius: 4,
                            border: '1px solid #cbd5e1', background: '#fffbeb', fontSize: 11, color: '#0f172a'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ padding: '0 12px 8px' }}>
                      <button style={{
                        padding: '6px 18px', borderRadius: 5, border: 'none',
                        background: '#006a4e', color: '#ffffff', fontSize: 11, fontWeight: 800,
                        boxShadow: currentStepIndex === 3 ? '0 0 12px rgba(0,106,78,0.7)' : '0 1px 3px rgba(0,106,78,0.2)',
                        outline: currentStepIndex === 3 ? '2.5px solid #34d399' : 'none',
                        transform: currentStepIndex === 3 ? 'scale(0.97)' : 'none',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}>
                        Confirm Fab Inhouse
                      </button>
                    </div>
                  </div>

                  {/* FABRIC & SHADE BREAKDOWN TABLE (EXACT USER SCREENSHOT) */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                      FABRIC & SHADE BREAKDOWN · 1 shade · 2,052 pcs · 2,462 mtrs
                    </div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflowX: 'auto', background: '#ffffff' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '1px solid #cbd5e1' }}>
                            <th style={{ padding: '5px 8px' }}>STYLE</th>
                            <th style={{ padding: '5px 8px' }}>SHADE</th>
                            <th style={{ padding: '5px 8px' }}>FABRIC DESCRIPTION</th>
                            <th style={{ padding: '5px 8px', textAlign: 'right' }}>ORDER PCS</th>
                            <th style={{ padding: '5px 8px', textAlign: 'right' }}>CONS (MTR/PC)</th>
                            <th style={{ padding: '5px 8px', textAlign: 'right' }}>APPROX FABRIC MTRS</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '5px 8px', fontWeight: 600 }}>KP PF TP CF HARRY TRACK</td>
                            <td style={{ padding: '5px 8px' }}>Black</td>
                            <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>BQ77B</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>2,052</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>1.20</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: '#0d9488' }}>2,462</td>
                          </tr>
                          <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                            <td style={{ padding: '5px 8px' }}>TOTAL</td>
                            <td style={{ padding: '5px 8px' }}></td>
                            <td style={{ padding: '5px 8px' }}></td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>2,052</td>
                            <td style={{ padding: '5px 8px' }}></td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#0d9488' }}>2,462</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* STAGE 4: SALES CONTRACT COST SHEET (EXACT CostSheetEntry)*/}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'costsheet' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#ffffff', position: 'relative', padding: '12px 18px', boxSizing: 'border-box' }}>
              {/* Header Bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #c3cbd5', paddingBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18, color: '#006a4e', fontWeight: 800 }}>←</span>
                    <span style={{ fontSize: 16 }}>🧾</span>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: '#006a4e' }}>
                      TAS/700 — Cost Sheet
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Lifestyle · {activeFactoryName} · Merch: Jasiya
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>ENQUIRY DATE</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155' }}>02-04-2026</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>TARGET FI (EST - 20D)</div>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: '#006a4e' }}>25-05-2026</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#006a4e', fontWeight: 800, textTransform: 'uppercase' }}>FACTORY FI DATE (+0D)</div>
                    <div style={{
                      height: 28, padding: '0 8px', borderRadius: 4, background: '#fffbeb',
                      border: currentStepIndex === 0 ? '2px solid #2563eb' : '1.5px solid #d4a72c',
                      fontSize: 11.5, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <span>14-06-2026</span>
                      <span>📅</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sub-header instruction */}
              <div style={{ padding: '6px 0 2px', display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Cost Grid</span>
                <span style={{ fontSize: 10.5, color: '#64748b' }}>enter value (yellow) · calculated automatically (grey) · $ 4 decimals · ₹ 3</span>
              </div>

              {/* Cost Card: Top Banner with COG & INR, 3 Column Groups */}
              <div style={{ border: '1px solid #c3cbd5', borderRadius: 10, background: '#ffffff', marginTop: 6, overflow: 'hidden' }}>
                {/* Top Banner (COG $ and INR ₹) */}
                <div style={{
                  padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #c3cbd5',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#006a4e' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#006a4e', letterSpacing: 0.8 }}>COSTING</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>COG $</span>
                      <span style={{ fontSize: 14, fontWeight: 900, color: '#006a4e' }}>5.0200</span>
                    </div>
                    <span style={{ color: '#cbd5e1' }}>|</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>INR ₹</span>
                      <span style={{ fontSize: 14, fontWeight: 900, color: '#006a4e' }}>481.920</span>
                    </div>
                  </div>
                </div>

                {/* 3 Columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 1.1fr', gap: 12, padding: 12 }}>
                  {/* Col 1: Fabric */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', background: '#fafbfc' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', letterSpacing: 0.6, marginBottom: 8 }}>
                      FABRIC COSTING
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>FAB PRICE ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>2.0000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>FREIGHT ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.3000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>CIF ($) (auto)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 800, color: '#006a4e' }}>2.3000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>CONSUMPTION (MTR)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>1.2000</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #cbd5e1' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>TOTAL FABRIC COST ($) (auto)</div>
                      <div style={{ height: 24, padding: '0 6px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 800, color: '#006a4e' }}>2.7600</div>
                    </div>
                  </div>

                  {/* Col 2: Manufacturing Cost (CM & Trims) */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', background: '#fafbfc' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', letterSpacing: 0.6, marginBottom: 8 }}>
                      MANUFACTURING COST
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>SEWING CM ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>1.0000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>BD TRIMS ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.8000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>IND TRIMS ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.1000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>CHINA TRIMS ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.1000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>WASHING ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.1500</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>TRANSPORT ($)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>0.1100</div>
                      </div>
                    </div>
                  </div>

                  {/* Col 3: Total & Currency */}
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', background: '#fafbfc' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', letterSpacing: 0.6, marginBottom: 8 }}>
                      TOTAL &amp; EXCHANGE
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>COG ($) (auto)</div>
                        <div style={{ height: 26, padding: '0 8px', background: '#f1f5f9', border: '1.5px solid #cbd5e1', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 900, color: '#006a4e' }}>5.0200</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>EXCHANGE RATE (₹/$)</div>
                        <div style={{ height: 24, padding: '0 6px', background: '#fffbeb', border: '1px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700 }}>96.000</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>TOTAL INR (₹) (auto)</div>
                        <div style={{ height: 28, padding: '0 8px', background: '#f1f5f9', border: '1.5px solid #cbd5e1', borderRadius: 4, display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 900, color: '#006a4e' }}>₹ 481.920</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Actions */}
              <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 10 }}>
                <button style={{ padding: '6px 16px', borderRadius: 5, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontSize: 11, fontWeight: 600 }}>
                  Cancel
                </button>
                <button style={{
                  padding: '6px 22px', borderRadius: 5, border: 'none',
                  background: '#006a4e', color: '#ffffff', fontSize: 11.5, fontWeight: 800,
                  boxShadow: currentStepIndex === 2 ? '0 0 14px rgba(0,106,78,0.7)' : '0 2px 4px rgba(0,106,78,0.2)',
                  outline: currentStepIndex === 2 ? '2.5px solid #34d399' : 'none',
                  cursor: 'pointer'
                }}>
                  Save Cost Sheet
                </button>
              </div>
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* STAGE 5: FINAL COSTING (EXACT ERP FinalCostEntry SCREEN) */}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'finalcosting' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#ffffff', position: 'relative', padding: '12px 18px', boxSizing: 'border-box' }}>
              {/* Header Bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #c3cbd5', paddingBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18, color: '#b5651d', fontWeight: 800 }}>←</span>
                    <span style={{ fontSize: 16 }}>📑</span>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: '#b5651d' }}>
                      TAS/728 — Final Costing
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Lifestyle · {activeFactoryName} · Merch: Jasiya · Order: 2,052 Pcs
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>ENQUIRY DATE</div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155' }}>02-04-2026</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>TARGET FI (-20D)</div>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: '#b5651d' }}>25-05-2026</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#006a4e', fontWeight: 800 }}>FACTORY FI DATE (+20D)</div>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: '#006a4e' }}>14-06-2026</div>
                  </div>
                </div>
              </div>

              {/* Sub-header instruction */}
              <div style={{ padding: '6px 0 2px', display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Final Cost Grid</span>
                <span style={{ fontSize: 10.5, color: '#64748b' }}>Pre-filled from the approved Sales Contract Cost Sheet — change what differs in production · $ 4 decimals · ₹ 3</span>
              </div>

              {/* Cost Card: Top Banner with COG & INR, 3 Column Groups */}
              <div style={{ border: '1px solid #c3cbd5', borderRadius: 10, background: '#ffffff', marginTop: 6, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '7px 14px', background: '#b5651d14', borderBottom: '1px solid #c3cbd5' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#b5651d', letterSpacing: 1 }}>FINAL COSTING</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: '#475569' }}>COG $</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>5.0200</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '3px 10px', borderRadius: 6, background: '#b5651d', color: '#fff' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6 }}>INR ₹</span>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>481.920</span>
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, padding: 10 }}>
                  {/* Group 1: Fabric Costing */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc', overflow: 'hidden' }}>
                    <div style={{ padding: '5px 10px', background: '#e2e8f0', borderBottom: '1px solid #cbd5e1', fontSize: 11, fontWeight: 800, color: '#334155' }}>FABRIC COSTING</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 8 }}>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>FABRIC RATE $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>2.0000</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>FREIGHT $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.1500</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>CIF $</span>
                        <div style={{ height: 26, background: '#eef2ff', border: '1.5px solid #c7d2fe', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 800, fontSize: 11.5, color: '#1A4F9C' }}>2.1500</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>CONSUMPTION</span>
                        <div style={{
                          height: 26, background: '#fffbeb', border: currentStepIndex === 1 ? '2px solid #b5651d' : '1.5px solid #d4a72c',
                          borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 700, fontSize: 12,
                          boxShadow: currentStepIndex === 1 ? '0 0 10px rgba(181,101,29,0.5)' : 'none'
                        }}>
                          1.2000
                        </div>
                      </div>
                      <div style={{ gridColumn: 'span 2' }}>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>FABRIC COST $</span>
                        <div style={{ height: 26, background: '#eef2ff', border: '1.5px solid #c7d2fe', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 800, fontSize: 12, color: '#1A4F9C' }}>2.5800</div>
                      </div>
                    </div>
                  </div>

                  {/* Group 2: Manufacturing Cost */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc', overflow: 'hidden' }}>
                    <div style={{ padding: '5px 10px', background: '#e2e8f0', borderBottom: '1px solid #cbd5e1', fontSize: 11, fontWeight: 800, color: '#334155' }}>MANUFACTURING COST</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 8 }}>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>CM $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>1.1000</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>BD DEPT $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.2000</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>INDIA DEPT $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.1500</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>CHINA DEPT $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.0500</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>WASHING $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.3000</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>TRANSPORT $</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 600, fontSize: 11.5 }}>0.1000</div>
                      </div>
                    </div>
                  </div>

                  {/* Group 3: Total & Exchange */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc', overflow: 'hidden' }}>
                    <div style={{ padding: '5px 10px', background: '#e2e8f0', borderBottom: '1px solid #cbd5e1', fontSize: 11, fontWeight: 800, color: '#334155' }}>TOTAL &amp; EXCHANGE</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8 }}>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>TOTAL COG $</span>
                        <div style={{ height: 28, background: '#eef2ff', border: '1.5px solid #c7d2fe', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontWeight: 800, fontSize: 13, color: '#b5651d' }}>5.0200</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>EXCHANGE RATE</span>
                        <div style={{ height: 26, background: '#fffbeb', border: '1.5px solid #d4a72c', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontWeight: 700, fontSize: 12 }}>96.000</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: '#475569', display: 'block' }}>FINAL INR (₹)</span>
                        <div style={{ height: 30, background: '#ecfdf5', border: '1.5px solid #a7f3d0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', fontWeight: 800, fontSize: 14, color: '#006a4e' }}>₹481.920</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Actions */}
              <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 10 }}>
                <button style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontSize: 11.5, fontWeight: 600 }}>Cancel</button>
                <button style={{
                  padding: '7px 24px', borderRadius: 6, border: 'none',
                  background: '#b5651d', color: '#ffffff', fontSize: 12, fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: currentStepIndex === 2 ? '0 0 14px rgba(181,101,29,0.7)' : '0 2px 4px rgba(181,101,29,0.3)',
                  outline: currentStepIndex === 2 ? '2.5px solid #fdba74' : 'none'
                }}>
                  Save Final Costing
                </button>
              </div>
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* STAGE 6: SHIPMENT (EXACT ERP ShipmentView SCREEN)        */}
          {/* -------------------------------------------------------- */}
          {moduleData.type === 'shipment' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#ffffff', position: 'relative', padding: '12px 18px', boxSizing: 'border-box' }}>
              {/* Header Bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                <span style={{ fontSize: 18, color: '#006a4e', fontWeight: 800 }}>←</span>
                <span style={{ fontSize: 18 }}>⛴</span>
                <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#006a4e' }}>
                  {activeFactoryName} — Shipment
                </div>
              </div>

              {/* Tabs: + New Shipment (Active) | Saved Shipments */}
              <div style={{ display: 'flex', borderBottom: '1px solid #cbd5e1', margin: '4px 0 8px' }}>
                <div style={{ padding: '6px 14px', borderBottom: '3px solid #006a4e', color: '#006a4e', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  ＋ New Shipment
                </div>
                <div style={{ padding: '6px 14px', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  📦 Saved Shipments <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 8, background: '#f1f5f9', color: '#475569', fontSize: 10 }}>12</span>
                </div>
              </div>

              {/* Row 1: Ex-Factory Date & Remark */}
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, marginBottom: 8 }}>
                <div>
                  <label style={{ fontSize: 9.5, letterSpacing: 0.8, color: '#475569', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3, display: 'block' }}>EX FACTORY DATE</label>
                  <input readOnly value="31-10-2026" style={{
                    width: '100%', height: 28, padding: '0 8px', background: '#ffffff', border: currentStepIndex === 0 ? '2px solid #006a4e' : '1px solid #cbd5e1',
                    borderRadius: 4, fontSize: 11.5, fontWeight: 600, boxSizing: 'border-box'
                  }} />
                  <span style={{ fontSize: 9, color: '#94a3b8' }}>defaults to today</span>
                </div>
                <div>
                  <label style={{ fontSize: 9.5, letterSpacing: 0.8, color: '#475569', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3, display: 'block' }}>REMARK</label>
                  <input readOnly value="Export Dispatch Challan #CH-8821 · Container 40ft" style={{
                    width: '100%', height: 28, padding: '0 8px', background: '#ffffff', border: '1px solid #cbd5e1',
                    borderRadius: 4, fontSize: 11.5, boxSizing: 'border-box'
                  }} />
                </div>
              </div>

              {/* Row 2: Select TAS Pills */}
              <div style={{ marginBottom: 8, background: '#f8fafc', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 10, letterSpacing: 0.6, color: '#475569', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                  SELECT TAS (1 SELECTED) · FINAL COSTING APPROVED ONLY
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  <span style={{
                    padding: '4px 12px', borderRadius: 14, fontSize: 11, fontWeight: 700,
                    background: '#006a4e', color: '#ffffff', border: '1px solid #006a4e',
                    boxShadow: currentStepIndex === 1 ? '0 0 10px rgba(0,106,78,0.6)' : 'none', cursor: 'pointer'
                  }}>
                    TAS/728 ✓
                  </span>
                  {['TAS/700', 'TAS/725', 'TAS/756', 'TAS/830'].map(t => (
                    <span key={t} style={{
                      padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600,
                      background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', cursor: 'pointer'
                    }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Row 3: Shade Breakdown Card */}
              <div style={{ border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ padding: '6px 10px', background: '#006a4e12', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 800, color: '#006a4e', fontSize: 12 }}>TAS/728</span>
                  <span style={{ fontSize: 10.5, color: '#64748b' }}>Lifestyle · 1 shade</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', color: '#475569', textTransform: 'uppercase', fontSize: 9.5, letterSpacing: 0.6, borderBottom: '1px solid #cbd5e1' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>Shade</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Order Qty</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Prev Ship</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', background: '#fffbeb', width: 95 }}>Ship Qty</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 600 }}>Black</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#64748b' }}>2,052</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#64748b' }}>0</td>
                      <td style={{ padding: '2px 8px', textAlign: 'right', background: '#fffbeb' }}>
                        <div style={{
                          height: 22, background: '#fffbe6', border: currentStepIndex === 2 ? '2px solid #006a4e' : '1.5px solid #006a4e',
                          borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 6px', fontWeight: 800, color: '#0f172a',
                          boxShadow: currentStepIndex === 2 ? '0 0 10px rgba(0,106,78,0.5)' : 'none'
                        }}>
                          2,052
                        </div>
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: '#15803d' }}>0</td>
                    </tr>
                    <tr style={{ background: '#f8fafc', fontWeight: 800, borderTop: '2px solid #cbd5e1' }}>
                      <td style={{ padding: '4px 8px' }}>TOTAL</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#64748b' }}>2,052</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#64748b' }}>0</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#006a4e' }}>2,052</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#15803d' }}>0</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Row 4: Attach Soft Copies */}
              <div style={{ border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '5px 10px', background: '#006a4e12', fontWeight: 700, color: '#006a4e', fontSize: 11 }}>
                  Attach Soft Copies
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 80px', fontSize: 10.5, borderTop: '1px solid #cbd5e1', padding: '4px 8px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>Commercial Invoice</span>
                  <span style={{ color: '#006a4e', fontWeight: 700 }}>📎 INV-2026-0842.pdf</span>
                  <span style={{ textAlign: 'center', padding: '2px 6px', borderRadius: 4, background: '#006a4e14', color: '#006a4e', fontWeight: 700 }}>Replace</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 80px', fontSize: 10.5, borderTop: '1px solid #f1f5f9', padding: '4px 8px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>Packing List</span>
                  <span style={{ color: '#006a4e', fontWeight: 700 }}>📎 PL-728-FINAL.pdf</span>
                  <span style={{ textAlign: 'center', padding: '2px 6px', borderRadius: 4, background: '#006a4e14', color: '#006a4e', fontWeight: 700 }}>Replace</span>
                </div>
              </div>

              {/* Bottom Actions */}
              <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 10 }}>
                <button style={{
                  padding: '7px 26px', borderRadius: 6, border: 'none',
                  background: '#006a4e', color: '#ffffff', fontSize: 12, fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: currentStepIndex === 3 ? '0 0 14px rgba(0,106,78,0.7)' : '0 2px 4px rgba(0,106,78,0.3)',
                  outline: currentStepIndex === 3 ? '2.5px solid #6ee7b7' : 'none'
                }}>
                  Save Shipment
                </button>
              </div>
            </div>
          )}

          {/* -------------------------------------------------------- */}
          {/* ANIMATED SVG MOUSE CURSOR & CLICK RIPPLE                 */}
          {/* -------------------------------------------------------- */}
          {step.cursor && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                transform: `translate(${step.cursor.x}px, ${step.cursor.y}px)`,
                transition: `transform ${0.7 / playbackSpeed}s cubic-bezier(0.45, 0, 0.2, 1)`,
                pointerEvents: 'none',
                zIndex: 60
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 3L19 12L12 14L9 21L5 3Z"
                  fill="#0f172a"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {/* Click Ripple */}
          {ripplePos && (
            <div
              key={rippleKey}
              style={{
                position: 'absolute',
                left: ripplePos.x,
                top: ripplePos.y,
                width: 32,
                height: 32,
                marginLeft: -16,
                marginTop: -16,
                borderRadius: '50%',
                border: '2px solid #2563eb',
                pointerEvents: 'none',
                zIndex: 59,
                animation: 'rip 0.55s ease-out forwards'
              }}
            />
          )}

          {/* Saved Toast Notification */}
          {showToast && (
            <div style={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#047857',
              color: '#ffffff',
              padding: '8px 18px',
              borderRadius: 6,
              fontSize: 11.5,
              fontWeight: 700,
              boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              zIndex: 70,
              animation: 'toastUp 0.3s ease-out forwards'
            }}>
              <span>✓</span>
              <span>
                {moduleData.type === 'costsheet'
                  ? 'Sales Contract Cost Sheet Submitted to Bangladesh Team'
                  : moduleData.type === 'finalcosting'
                  ? 'Final Costing Audited & Profit Margin Locked'
                  : moduleData.type === 'shipment'
                  ? 'Vehicle Gate Pass GP-5591 Issued · EX FACT Completed'
                  : moduleData.type === 'fabinhouse'
                  ? 'Fabric Inhouse Confirmed for TAS/728 · Stage 4_9 Synced in Central TNA'
                  : 'Daily Production Entry #5582 Saved · TNA Milestones Synced'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* 4. BOTTOM NARRATION CARD & TRANSPORT CONTROLS                */}
      {/* ============================================================ */}
      <div style={{
        padding: '10px 20px',
        background: '#0f172a',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexShrink: 0
      }}>
        {/* Role & Narration */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11,
            fontFamily: 'monospace',
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: moduleData.roleColor || '#006a4e',
            border: `1.5px solid ${moduleData.roleColor || '#006a4e'}`,
            borderRadius: 5,
            padding: '4px 10px',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            {moduleData.role}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {step.title}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              "{step.narration[language] || step.narration.en}"
            </div>
          </div>
        </div>

        {/* Transport Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Previous Step */}
          <button
            className="walk-ctl"
            onClick={() => handleStepJump(Math.max(0, currentStepIndex - 1))}
            disabled={currentStepIndex === 0}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.15)',
              background: '#1e293b',
              color: currentStepIndex === 0 ? '#64748b' : '#f8fafc',
              fontSize: 12,
              fontWeight: 700,
              cursor: currentStepIndex === 0 ? 'default' : 'pointer'
            }}
          >
            ⏮ Prev
          </button>

          {/* Play / Pause */}
          <button
            className="walk-ctl"
            onClick={handlePlayPause}
            style={{
              padding: '6px 18px',
              borderRadius: 6,
              border: 'none',
              background: isPlaying ? '#e0f2fe' : '#2563eb',
              color: isPlaying ? '#0369a1' : '#ffffff',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              minWidth: 84
            }}
          >
            {isPlaying ? '⏸ Pause' : '▶ Play'}
          </button>

          {/* Next Step */}
          <button
            className="walk-ctl"
            onClick={() => handleStepJump(Math.min(totalSteps - 1, currentStepIndex + 1))}
            disabled={currentStepIndex === totalSteps - 1}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.15)',
              background: '#1e293b',
              color: currentStepIndex === totalSteps - 1 ? '#64748b' : '#f8fafc',
              fontSize: 12,
              fontWeight: 700,
              cursor: currentStepIndex === totalSteps - 1 ? 'default' : 'pointer'
            }}
          >
            Next ⏭
          </button>

          {/* Restart */}
          <button
            className="walk-ctl"
            onClick={() => handleStepJump(0)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.15)',
              background: '#1e293b',
              color: '#94a3b8',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            ↺ Restart
          </button>
        </div>
      </div>
    </div>
  );
}
